import PropTypes from "prop-types";
import classNames from "classnames";
import useBackInStock from "../../hooks/useBackInStock";
import { useVariant } from "../../context/variant/VariantContext";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import Button from "../buttons/button/Button";
import { BUTTON_TYPES } from "../buttons/button/ButtonTypes";
import {
  BACK_IN_STOCK_BUTTON_ID,
  TEXT_SIZES,
  PRIORITY_BACK_IN_STOCK,
  ANON_BIS_BUCKET,
} from "../../constants";
import styles from "./NotificationButton.scss";
import { VARIANT_CONFIGS } from "../anonymousBackInStock/variants/variantConfig";
import {
  ignoreBackInStockAnalytics,
  setBackInStockAnalytics,
} from "../../analytics/postPageLoad/backInStock";
import { useProduct } from "../../context/product/ProductContext";
import isRestockingSoon from "../../utils/restockingSoon";
import NotificationButtonMessage from "../notificationButtonMessage/NotificationButtonMessage";
import useLoyalty from "../../hooks/useLoyalty";
import { useAnonymousBackInStock } from "../../context/anonymousBackInStock/AnonymousBackInStockContext";
import { useFeaturesReady } from "../../context/featuresReady/FeaturesReadyContext";
import { getFeature } from "../../utils/features";
import getWindow from "../../utils/window";
import { getCustomerIsAuthenticated } from "../../utils/identity";
import { ERROR_TYPE, logError } from "../../utils/newRelicLogger";

const isKnownToBeInStock = ({ isInStock, selectedVariant }) =>
  (isInStock && !selectedVariant) || selectedVariant?.isInStock;

const createButton = ({
  iconClass,
  text,
  type,
  onClick,
  inStockProduct,
  shouldShowSave,
  shouldShowRestockingSoon,
  isLooksProduct,
  isMobile,
  isPriority,
  scope,
  disabled,
}) => {
  const hasSaveButton =
    shouldShowSave && (inStockProduct || shouldShowRestockingSoon);
  const isDesktopLooks = !isMobile && isLooksProduct;

  return (
    <>
      {renderButton({
        type,
        onClick,
        iconClass,
        text,
        isDesktopLooks,
        hasSaveButton,
        scope,
        disabled,
      })}
      {isPriority ? <NotificationButtonMessage /> : null}
    </>
  );
};

const renderButton = ({
  type,
  onClick,
  iconClass,
  text,
  isDesktopLooks,
  hasSaveButton,
  scope,
  disabled,
}) => (
  <Button
    type={scope === "rec_outfits" ? BUTTON_TYPES.secondary : type}
    id={BACK_IN_STOCK_BUTTON_ID}
    handleClick={onClick}
    size={isDesktopLooks ? TEXT_SIZES.london3 : TEXT_SIZES.london2}
    additionalClasses={classNames(styles.button, {
      [styles.desktopLooks]: isDesktopLooks,
      [styles.withSaveButton]: hasSaveButton,
    })}
    disabled={disabled}
  >
    <span
      className={classNames(
        scope === "rec_outfits" ? "product-email" : iconClass,
        styles.icon,
      )}
    ></span>
    <span className={styles.text}>{text}</span>
  </Button>
);

const NotificationButton = ({
  handleNotifyMeClick,
  isInStock,
  shouldShowSave,
  selectedProductId,
  isLooksProduct,
  scope,
}) => {
  const { selectedVariant } = useVariant();
  const {
    hasNotification,
    addVariantToNotifications,
    removeVariantFromNotifications,
  } = useBackInStock();
  const selectedVariantId = selectedVariant?.variantId;
  const { getTranslation, oosVariantsAreSelectable, isMobile } =
    useRuntimeConstants();
  const variantHasNotification = hasNotification(selectedVariantId);
  const { product } = useProduct();
  const hasBenefit = useLoyalty(PRIORITY_BACK_IN_STOCK);
  const { anonymousBackInStockProducts } = useAnonymousBackInStock();
  const { areFeaturesReady } = useFeaturesReady();

  if (!oosVariantsAreSelectable || isInStock === null) {
    return null;
  }

  if (isKnownToBeInStock({ isInStock, selectedVariant })) {
    ignoreBackInStockAnalytics();
    return null;
  }

  const commonParams = {
    inStockProduct: isInStock,
    shouldShowSave,
    isLooksProduct,
    isMobile,
    shouldShowRestockingSoon: isRestockingSoon(product),
  };

  if (variantHasNotification) {
    ignoreBackInStockAnalytics();
    return createButton({
      ...commonParams,
      type: BUTTON_TYPES.tertiary,
      onClick: () => {
        removeVariantFromNotifications({ variantId: selectedVariantId });
      },
      text: getTranslation("back_in_stock_disable_alert"),
      iconClass: "product-email",
      scope,
    });
  }

  setBackInStockAnalytics();

  return createButton({
    ...commonParams,
    type: BUTTON_TYPES.primary,
    onClick: async () => {
      handleNotifyMeClick();
      if (selectedVariantId) {
        let useAnonymousBIS = false;
        if (areFeaturesReady) {
          const feature = getFeature("pdp_anonymous_backinstock_enabled");
          const isEnabled = feature && feature.isEnabled();
          const bucket = feature?.getVariables()?.bucket;
          useAnonymousBIS =
            isEnabled &&
            bucket !== ANON_BIS_BUCKET.CONTROL &&
            bucket in VARIANT_CONFIGS;
        }
        if (useAnonymousBIS) {
          try {
            const isAuthenticated = await getCustomerIsAuthenticated();
            if (isAuthenticated) {
              addVariantToNotifications({
                variantId: selectedVariantId,
                productId: selectedProductId,
                isPriority: hasBenefit,
              });
            } else {
              const windowRef = getWindow();
              windowRef.asos.eventBus.emit("anonBisModalOpen", {
                focusOnCloseElement: windowRef.document.getElementById(
                  BACK_IN_STOCK_BUTTON_ID,
                ),
                selectedVariantId,
                selectedProductId,
              });
            }
          } catch (error) {
            logError(ERROR_TYPE.UserActionFailed, {
              action: "isAuthenticated",
              message: error instanceof Error ? error.message : String(error),
            });
          }
        } else {
          addVariantToNotifications({
            variantId: selectedVariantId,
            productId: selectedProductId,
            isPriority: hasBenefit,
          });
        }
      }
    },
    text: getTranslation("back_in_stock_notify_me"),
    iconClass: "product-email-white",
    isPriority: hasBenefit,
    scope,
    disabled:
      selectedVariantId &&
      anonymousBackInStockProducts?.includes(selectedVariantId),
  });
};

NotificationButton.propTypes = {
  handleNotifyMeClick: PropTypes.func.isRequired,
  isInStock: PropTypes.bool.isRequired,
  shouldShowSave: PropTypes.bool.isRequired,
  selectedProductId: PropTypes.number.isRequired,
  isLooksProduct: PropTypes.bool.isRequired,
  isPriority: PropTypes.bool,
  scope: PropTypes.string,
};

export default NotificationButton;
